import React, { useEffect, useState } from 'react';

import ArrowLeft from 'asset/c-l.svg';
import ArrowRight from 'asset/c-r.svg';
import cn from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import DOMPurify from 'isomorphic-dompurify';

import { descriptionVariants, framerCommon, titleVariants } from '../../framer.variants';
import { data_carousel } from './content';

export default function CarouselWithBg() {
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [current, setCurrent] = useState(emblaApi?.selectedScrollSnap() || 0);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);

  function scrollNext() {
    emblaApi.scrollNext();
  }

  function scrollPrev() {
    emblaApi.scrollPrev();
  }

  function trackChanges() {
    setCurrent(emblaApi?.selectedScrollSnap());

    setIsPrevDisabled(!emblaApi?.canScrollPrev());
    setIsNextDisabled(!emblaApi?.canScrollNext());
  }

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('scroll', trackChanges).on('init', trackChanges).on('reInit', trackChanges);
    }
  }, [emblaApi]);

  return (
    <section className="relative lg:my-8">
      <div className="gl-wrapper">
        <button disabled={isPrevDisabled} onClick={scrollPrev} className={cn('left-0 lg:hidden', btnStyles)}>
          <img className={cn(btnArrowStyles)} src={ArrowLeft} alt="" />
        </button>
        <button disabled={isNextDisabled} onClick={scrollNext} className={cn('right-[4rem] lg:hidden', btnStyles)}>
          <img className={cn(btnArrowStyles)} src={ArrowRight} alt="" />
        </button>
      </div>
      <div className="embla relative w-full overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex w-full gap-8 text-white">
          {data_carousel.map(({ prefix, title, description, image }, idx) => {
            const backgroundStyle = {
              backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(${image})`,
              backgroundPosition: idx % 2 === 0 ? '75%' : '20%',
            };
            return (
              <div
                key={title}
                style={backgroundStyle}
                className="embla__slide relative aspect-[1440/632] min-h-[632px] min-w-full basis-full overflow-hidden bg-cover bg-center bg-no-repeat"
              >
                {/*<img src={item.image} className="absolute -top-[20%]" />*/}
                <div className="gl-wrapper relative z-10 flex h-full max-w-[90rem] flex-col justify-center gap-11 px-[116px] lg:px-[40px] sm:px-4">
                  <div className="content max-w-[52%] transition-all duration-500 xlg:max-w-full">
                    <motion.span
                      {...framerCommon}
                      variants={titleVariants}
                      viewport={{ once: false }}
                      transition={{ duration: 1, delay: 0.25 }}
                      className="relative mb-0 block font-Proxima text-[1.5rem] font-black uppercase leading-[0.85] text-demo-xr-blue"
                    >
                      {prefix}
                    </motion.span>
                    <motion.h3
                      {...framerCommon}
                      viewport={{ once: false }}
                      variants={titleVariants}
                      className="relative font-Proxima text-[2.75rem] font-black uppercase leading-[1.09]"
                    >
                      {title}
                    </motion.h3>

                    <motion.div
                      {...framerCommon}
                      viewport={{ once: false }}
                      variants={descriptionVariants}
                      className="relative mt-[2.75rem] text-[1rem] leading-normal"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                    />
                  </div>
                  <div className="placeholder relative"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="relative -top-[100px] gap-4 lg:absolute lg:bottom-[64px] lg:top-[unset] lg:flex lg:items-center">
          <button
            disabled={isPrevDisabled}
            onClick={scrollPrev}
            className={cn('min-lg:hidden lg:static lg:h-8 lg:w-8 lg:translate-x-0', btnStyles)}
          >
            <img className="group-disabled:brightness-50" src={ArrowLeft} alt="" />
          </button>
          <div className="absolute flex gap-2 lg:static">
            {data_carousel.map(({ title }, idx) => {
              const isActive = idx === current;
              return (
                <div
                  key={title}
                  className={cn(
                    'h-[0.5rem] w-[0.75rem] bg-[#414045] transition-all duration-500',
                    isActive && '!bg-demo-xr-blue',
                  )}
                />
              );
            })}
          </div>
          <button
            disabled={isNextDisabled}
            onClick={scrollNext}
            className={cn('left-0 min-lg:hidden lg:static lg:h-8 lg:w-8 lg:translate-x-0', btnStyles)}
          >
            <img className="group-disabled:brightness-50" src={ArrowRight} alt="" />
          </button>
        </div>
      </div>
    </section>
  );
}

const btnArrowStyles = `relative -top-[2px] h-9 w-9 group-disabled:brightness-50`;
const btnStyles =
  'group absolute top-[45%] z-10 h-9 w-9 translate-x-[50%] border-2 border-transparent hover:border-demo-xr-blue disabled:hover:cursor-not-allowed disabled:hover:border-gray-80';
